import { z } from 'zod';
import { FBCallToAction } from './AdCreative';
import {
  CALLS_CALL_TO_ACTION,
  CREATIVE_IMPORT_LIMITS,
  isSupportedCallToAction,
} from './CreativeImport';

// What an employee can change on an already-created creative, from the review
// surface: its copy, and — only where the body explicitly says so — its media.
// The media half is opt-in and states each operation, so a copy edit can still
// never drop a carousel's cards or a dynamic ad's assets by omission.
//
// One schema serves three consumers — the server's runtime validation, the
// request type, and the frontend's field limits — so the caps below cannot
// drift from what the sinks accept. Limits are shared with the creative
// importer rather than restated: both write the same columns.

const CTA_MESSAGE = 'that call to action is not supported';

const callToActionSchema = z
  .nativeEnum(FBCallToAction)
  .refine(isSupportedCallToAction, { message: CTA_MESSAGE });

/**
 * The ad's own button, which unlike a pool entry may also be Call Now. Whether it
 * can actually dial — the format, the number, no lead form — depends on the rest
 * of the ad, so the server checks that against the ad as it would be saved.
 */
const adCallToActionSchema = z
  .nativeEnum(FBCallToAction)
  .refine(
    (cta) => cta === CALLS_CALL_TO_ACTION || isSupportedCallToAction(cta),
    {
      message: CTA_MESSAGE,
    },
  );

const creativeNameSchema = z
  .string()
  .min(1, { message: 'a creative name is required' })
  .max(CREATIVE_IMPORT_LIMITS.creativeNameMaxLength, {
    message: `a creative name must be ${CREATIVE_IMPORT_LIMITS.creativeNameMaxLength} characters or fewer`,
  });

/** The importer never writes a display URL, so this cap is its varchar(255) column's own. */
export const DISPLAY_LINK_MAX_LENGTH = 255;

const displayLinkSchema = z.string().max(DISPLAY_LINK_MAX_LENGTH, {
  message: `a display URL must be ${DISPLAY_LINK_MAX_LENGTH} characters or fewer`,
});

/**
 * A destination, bounded only by the varchar(255) sink it lands in. Not required
 * to parse as http(s): plenty of creatives predate any URL validation and carry
 * a scheme-less host, and refusing those here would mean a typo in a headline
 * couldn't be fixed without also rewriting the ad's link. The surface warns
 * instead — the same warn-and-carry-on the importer applies to a manifest URL.
 */
const destinationUrlSchema = z
  .string()
  .min(1, { message: 'a destination URL is required' })
  .max(CREATIVE_IMPORT_LIMITS.destinationUrlMaxLength, {
    message: `a destination URL must be ${CREATIVE_IMPORT_LIMITS.destinationUrlMaxLength} characters or fewer`,
  });

/** A card's own link, which may be cleared to fall back to the ad's. */
const slideUrlSchema = z.union([z.literal(''), destinationUrlSchema]);

const headlineSchema = z
  .string()
  .max(CREATIVE_IMPORT_LIMITS.headlineMaxLength, {
    message: `a headline must be ${CREATIVE_IMPORT_LIMITS.headlineMaxLength} characters or fewer`,
  });

/**
 * One existing carousel card's copy, addressed by its persisted id. Cards are
 * added and removed through `media.slides`, never by what this list omits.
 */
export const creativeCopySlidePatchSchema = z.object({
  id: z.number().int().positive(),
  headline: headlineSchema.optional(),
  description: headlineSchema.optional(),
  url: slideUrlSchema.optional(),
});

function textPool(entry: z.ZodTypeAny = z.string()) {
  return z
    .array(entry)
    .max(CREATIVE_IMPORT_LIMITS.dynamicMaxTextVariants, {
      message: `each copy pool can have at most ${CREATIVE_IMPORT_LIMITS.dynamicMaxTextVariants} variants`,
    })
    .optional();
}

/** A dynamic ad's copy: Facebook mixes one variant from each pool at delivery. */
export const creativeCopyPoolsPatchSchema = z.object({
  primaryTexts: textPool(),
  headlines: textPool(headlineSchema),
  descriptions: textPool(),
  callToActions: textPool(callToActionSchema),
});

/**
 * Resulting media counts an edit may leave behind. The wizard's bounds, not the
 * importer's: `CREATIVE_IMPORT_LIMITS.carouselSlidesMax` is a packaging promise
 * about generated packets, and applying it here would make an existing six-card
 * ad unfixable.
 */
export const CREATIVE_MEDIA_EDIT_LIMITS = {
  carouselSlidesMin: CREATIVE_IMPORT_LIMITS.carouselSlidesMin,
  carouselSlidesMax: 10,
  dynamicMediaMin: 1,
  dynamicMediaMax: CREATIVE_IMPORT_LIMITS.dynamicMaxMedia,
} as const;

/** An uploaded asset, by the table it landed in and its id there. */
export const creativeMediaRefSchema = z.object({
  mediaType: z.enum(['image', 'video']),
  mediaID: z.number().int().positive(),
});

/**
 * Media changes as operations against the current set, never as a replacement
 * snapshot: a snapshot would delete a card another editor added between the read
 * and the save, purely by not mentioning it.
 *
 * Dynamic replacement is a removal plus an addition — the assets are a set with
 * no per-slot identity to address.
 */
const creativeMediaPatchSchema = z.object({
  /** Single ads only. Swapping an image for a video also flips the format. */
  single: creativeMediaRefSchema.optional(),
  /** Carousel only. */
  slides: z
    .object({
      replace: z
        .array(
          creativeMediaRefSchema.extend({ id: z.number().int().positive() }),
        )
        .optional(),
      add: z
        .array(
          creativeMediaRefSchema.extend({
            headline: headlineSchema.optional(),
            description: headlineSchema.optional(),
            url: slideUrlSchema.optional(),
          }),
        )
        .optional(),
      remove: z.array(z.number().int().positive()).optional(),
    })
    .optional(),
  /** Dynamic only. */
  dynamic: z
    .object({
      add: z.array(creativeMediaRefSchema).optional(),
      remove: z.array(creativeMediaRefSchema).optional(),
    })
    .optional(),
});

/**
 * Every field is optional, and an absent field means "leave it alone" rather
 * than "clear it" — the surface sends whole drafts, but a partial body is a
 * valid request and the server treats an unchanged one as a no-op.
 */
export const creativeCopyPatchSchema = z.object({
  name: creativeNameSchema.optional(),
  /** Single image and video only; ignored for every other format. */
  graphicText: z.string().optional(),
  primaryText: z.string().optional(),
  headline: headlineSchema.optional(),
  description: z.string().optional(),
  // `null` is an explicit clear. `undefined`/absence still means leave the
  // existing CTA alone, which matters for genuinely partial API callers.
  callToAction: adCallToActionSchema.nullable().optional(),
  /**
   * The number a Call Now button dials. Ignored behind any other button, which
   * never keeps one.
   */
  callPhoneNumber: z.string().optional(),
  /** A lead form of this business, by id; `null` detaches the ad's lead form. */
  leadCardID: z.number().int().positive().nullable().optional(),
  url: destinationUrlSchema.optional(),
  displayLink: displayLinkSchema.optional(),
  /** Carousel only; ignored for every other format. */
  slides: z.array(creativeCopySlidePatchSchema).optional(),
  /** Dynamic only; ignored for every other format. */
  pools: creativeCopyPoolsPatchSchema.optional(),
  /** Absent means the ad's media is untouched. */
  media: creativeMediaPatchSchema.optional(),
});

export type CreativeCopySlidePatch = z.infer<
  typeof creativeCopySlidePatchSchema
>;
export type CreativeCopyPoolsPatch = z.infer<
  typeof creativeCopyPoolsPatchSchema
>;
export type CreativeCopyPatch = z.infer<typeof creativeCopyPatchSchema>;
export type CreativeMediaRef = z.infer<typeof creativeMediaRefSchema>;
export type CreativeMediaPatch = z.infer<typeof creativeMediaPatchSchema>;
export type CreativeSlideAdd = NonNullable<
  NonNullable<CreativeMediaPatch['slides']>['add']
>[number];
